import { useCallback, useMemo } from 'react';
import type {
	AdditionalMetadata,
	ClickedElementAttributes,
	ActionTypeAttributes,
} from '@post-office/message-template-contracts/message-instance-event';

export type FireAnalyticsEventPayload = {
	eventType: string;
	action?: string;
	actionSubject?: string;
	actionSubjectId?: string;
	attributes?: Record<string, unknown>;
};

export type MessageInstanceClickedPayload = {
	messageEventType: 'clicked';
	clickedElement: ClickedElementAttributes;
	action: ActionTypeAttributes;
	additionalMetadataFromFrontend: AdditionalMetadata;
};

export type HandlersParams = {
	fireAnalyticsEvent: (event: FireAnalyticsEventPayload) => void;
	messageInstanceClicked: (event: MessageInstanceClickedPayload) => void | Promise<void>;
	onDismissed: () => void;
	setIsModalOpen: (isOpen: boolean) => void;
};

export const useRovoAwarenessHandlers = ({
	fireAnalyticsEvent,
	messageInstanceClicked,
	onDismissed,
	setIsModalOpen,
}: HandlersParams): {
	handleDismiss: () => void;
	handleAskRovoClick: () => void;
	handleMoreIdeasClick: () => void;
} => {
	const handleDismiss = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismissBanner',
				clickedElement: 'cross-icon',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessHomeBannerDismiss',
		});

		onDismissed();
	}, [fireAnalyticsEvent, messageInstanceClicked, onDismissed]);

	const handleAskRovoClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'askRovo',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessHomeBannerAskRovo',
		});

		// Ask Rovo handler - can be expanded as needed
	}, [fireAnalyticsEvent, messageInstanceClicked]);

	const handleMoreIdeasClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'moreIdeas',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessHomeBannerMoreIdeas',
		});

		setIsModalOpen(true);
	}, [fireAnalyticsEvent, messageInstanceClicked, setIsModalOpen]);

	return useMemo(
		() => ({
			handleDismiss,
			handleAskRovoClick,
			handleMoreIdeasClick,
		}),
		[handleDismiss, handleAskRovoClick, handleMoreIdeasClick],
	);
};
